import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  UserPlus, Send, Clock, CheckCircle, XCircle, RefreshCw, 
  Trash2, ChevronRight, Award, Gift, Users, ArrowLeft, Loader2,
  Mail, MapPin, Briefcase, AlertCircle, HandHelping, TrendingUp
} from 'lucide-react';
import { useAuth, axiosInstance as axios } from '../context/AuthContext';
import Header from '../components/Header';
import InviteProfessionalModal from '../components/Modals/InviteProfessionalModal';
import { RequestProButton } from '../components/professionals/RequestProButton';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

const SPECIALTY_LABELS = {
  financial_planner: 'Financial Planner',
  estate_planner: 'Estate Planner',
  attorney: 'Attorney',
  genealogist: 'Genealogist',
  family_counselor: 'Family Counselor',
  mediator: 'Mediator',
  other: 'Other'
};

const STATUS_CONFIG = {
  pending: { 
    label: 'Pending', 
    icon: Clock, 
    color: 'text-amber-600', 
    bg: 'bg-amber-50',
    border: 'border-amber-200'
  },
  signed_up: { 
    label: 'Signed Up', 
    icon: CheckCircle, 
    color: 'text-green-600', 
    bg: 'bg-green-50',
    border: 'border-green-200'
  },
  active: { 
    label: 'Active', 
    icon: CheckCircle, 
    color: 'text-blue-600', 
    bg: 'bg-blue-50',
    border: 'border-blue-200'
  },
  expired: { 
    label: 'Expired', 
    icon: XCircle, 
    color: 'text-gray-500', 
    bg: 'bg-gray-50',
    border: 'border-gray-200'
  },
  cancelled: { 
    label: 'Cancelled', 
    icon: XCircle, 
    color: 'text-red-500', 
    bg: 'bg-red-50',
    border: 'border-red-200'
  }
};

function MyInvitations() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [invites, setInvites] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  const [myRequests, setMyRequests] = useState([]);
  const [demandSummary, setDemandSummary] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [invitesRes, statsRes, requestsRes, demandRes] = await Promise.all([
        axios.get(`${API}/professional-invites`, { withCredentials: true }),
        axios.get(`${API}/professional-invites/stats`, { withCredentials: true }),
        axios.get(`${API}/professional-requests`, { withCredentials: true }),
        axios.get(`${API}/professional-requests/demand/summary`)
      ]);
      setInvites(invitesRes.data);
      setStats(statsRes.data);
      setMyRequests(requestsRes.data);
      setDemandSummary(demandRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async (inviteId) => {
    setResendingId(inviteId);
    try {
      await axios.post(`${API}/professional-invites/${inviteId}/resend`, {}, { withCredentials: true });
      fetchData();
    } catch (err) {
      console.error('Error resending invite:', err);
      alert(err.response?.data?.detail || 'Failed to resend invitation');
    } finally {
      setResendingId(null);
    }
  };

  const handleCancel = async (inviteId) => {
    if (!window.confirm('Are you sure you want to cancel this invitation?')) return;
    
    setCancellingId(inviteId);
    try {
      await axios.delete(`${API}/professional-invites/${inviteId}`, { withCredentials: true });
      fetchData();
    } catch (err) {
      console.error('Error cancelling invite:', err);
      alert(err.response?.data?.detail || 'Failed to cancel invitation');
    } finally {
      setCancellingId(null);
    }
  };

  const handleInviteSuccess = () => {
    fetchData();
  };

  const handleCancelRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to cancel this request?')) return;
    
    try {
      await axios.delete(`${API}/professional-requests/${requestId}`, { withCredentials: true });
      fetchData();
    } catch (err) {
      console.error('Error cancelling request:', err);
      alert(err.response?.data?.detail || 'Failed to cancel request');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header variant="dashboard" />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-blue-600" size={40} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="my-invitations-page">
      <Header variant="dashboard" />

      <main className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6 transition"
          data-testid="back-btn"
        >
          <ArrowLeft size={20} className="mr-2" />
          Back
        </button>

        {/* Header Section */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Professional Network</h1>
            <p className="text-gray-600 mt-1">Help grow our network of trusted professionals</p>
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setShowInviteModal(true)}
                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 font-semibold shadow-md transition"
                data-testid="invite-professional-btn"
              >
                <UserPlus size={20} />
                Invite a Professional
              </button>
              <RequestProButton testId="request-professional-btn" onSuccess={fetchData} />
            </div>
            {/* Button explanations */}
            <div className="flex flex-wrap gap-4 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                <strong>Invite:</strong> Send an invite to someone you know
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                <strong>Request:</strong> Tell us what you need in your area
              </span>
            </div>
          </div>
        </div>

        {/* Referral Rewards Banner - Make it prominent */}
        <Card className="mb-6 border-0 shadow-md bg-gradient-to-r from-purple-500 to-indigo-600 text-white">
          <CardContent className="p-5">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center shrink-0">
                  <Gift className="text-white" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Earn Rewards for Referrals!</h3>
                  <p className="text-purple-100 text-sm">
                    Invite professionals you trust. When they join, you earn subscription discounts!
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-3 text-sm">
                <span className="bg-white/20 px-3 py-1 rounded-full">1st = 20% off</span>
                <span className="bg-white/20 px-3 py-1 rounded-full">3rd = 1 month free</span>
                <span className="bg-white/20 px-3 py-1 rounded-full">5+ = Permanent 10% off</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="border-0 shadow-md">
              <CardContent className="p-4 text-center">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Send className="text-blue-600" size={20} />
                </div>
                <div className="text-2xl font-bold text-gray-900">{stats.total_invites_sent}</div>
                <div className="text-sm text-gray-500">Invites Sent</div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md">
              <CardContent className="p-4 text-center">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Clock className="text-amber-600" size={20} />
                </div>
                <div className="text-2xl font-bold text-gray-900">{stats.pending_invites}</div>
                <div className="text-sm text-gray-500">Pending</div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md">
              <CardContent className="p-4 text-center">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <CheckCircle className="text-green-600" size={20} />
                </div>
                <div className="text-2xl font-bold text-gray-900">{stats.successful_referrals}</div>
                <div className="text-sm text-gray-500">Signed Up</div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md bg-gradient-to-br from-purple-50 to-indigo-50">
              <CardContent className="p-4 text-center">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Award className="text-purple-600" size={20} />
                </div>
                <div className="text-2xl font-bold text-purple-900">
                  {stats.current_discount_percent > 0 ? `${stats.current_discount_percent}%` : '-'}
                </div>
                <div className="text-sm text-purple-600">Current Discount</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Platform Demand Summary */}
        {demandSummary && demandSummary.total_active_requests > 0 && (
          <Card className="mb-8 border-0 shadow-md bg-gradient-to-r from-emerald-50 to-teal-50">
            <CardContent className="p-5">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center shrink-0">
                  <TrendingUp className="text-emerald-600" size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-emerald-900 mb-2">
                    Community Demand: {demandSummary.total_active_requests} families waiting for professionals
                  </h3>
                  <div className="flex flex-wrap gap-4 text-sm">
                    {demandSummary.by_specialty?.slice(0, 3).map((item, idx) => (
                      <div key={idx} className="bg-white/60 px-3 py-1 rounded-full border border-emerald-200">
                        <span className="text-emerald-700">{item.specialty_label}:</span>
                        <span className="font-semibold text-emerald-900 ml-1">{item.count} requests</span>
                      </div>
                    ))}
                  </div>
                  {demandSummary.top_cities?.length > 0 && (
                    <p className="text-xs text-emerald-600 mt-2">
                      Top areas: {demandSummary.top_cities.slice(0, 3).map(c => `${c.city}${c.region ? `, ${c.region}` : ''}`).join(' • ')}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* My Requests Section */}
        {myRequests.length > 0 && (
          <Card className="mb-8 border-0 shadow-md">
            <CardHeader className="border-b">
              <CardTitle className="text-lg flex items-center gap-2">
                <HandHelping size={20} className="text-emerald-600" />
                My Requests ({myRequests.filter(r => r.status === 'active').length} active)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                {myRequests.filter(r => r.status === 'active').map((request) => (
                  <div key={request.request_id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                        <Briefcase className="text-emerald-600" size={18} />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">
                          {SPECIALTY_LABELS[request.specialty] || request.specialty}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center gap-2">
                          <MapPin size={14} />
                          {request.city}{request.region && `, ${request.region}`}
                          <span className={`px-2 py-0.5 rounded-full text-xs ${
                            request.urgency === 'urgent' ? 'bg-red-100 text-red-700' :
                            request.urgency === 'soon' ? 'bg-amber-100 text-amber-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {request.urgency === 'urgent' ? '⚡ Urgent' :
                             request.urgency === 'soon' ? '📅 Soon' : '🕐 No Rush'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleCancelRequest(request.request_id)}
                      className="flex items-center gap-1 px-2 py-1.5 sm:p-2 text-red-600 hover:bg-red-50 rounded-lg transition text-xs sm:text-sm"
                      title="Cancel request"
                    >
                      <Trash2 size={18} />
                      <span className="sm:hidden">Cancel</span>
                    </button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Next Milestone Progress */}
        {stats?.next_milestone && (
          <Card className="mb-8 border-0 shadow-md bg-gradient-to-r from-amber-50 to-orange-50">
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center shrink-0">
                  <Gift className="text-amber-600" size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-amber-900">Next Milestone: {stats.next_milestone.name}</h3>
                  <p className="text-sm text-amber-700">{stats.next_milestone.description}</p>
                  <div className="mt-2">
                    <div className="flex justify-between text-xs text-amber-600 mb-1">
                      <span>{stats.next_milestone.progress} of {stats.next_milestone.required_referrals} referrals</span>
                      <span>{stats.next_milestone.remaining} more to go!</span>
                    </div>
                    <div className="h-2 bg-amber-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full transition-all duration-500"
                        style={{ width: `${(stats.next_milestone.progress / stats.next_milestone.required_referrals) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Badges Section */}
        {stats?.badges?.length > 0 && (
          <Card className="mb-8 border-0 shadow-md">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Award size={20} className="text-purple-600" />
                Your Badges
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              {stats.badges.map((badge, idx) => (
                <Badge key={idx} className="bg-purple-100 text-purple-700 px-3 py-1">
                  {badge}
                </Badge>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Invitations List */}
        <Card className="border-0 shadow-md">
          <CardHeader className="border-b">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users size={20} className="text-gray-600" />
              Invitation History ({invites.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {invites.length === 0 ? (
              <div className="py-12 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <UserPlus className="text-gray-400" size={32} />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">No Invitations Yet</h3>
                <p className="text-gray-500 mb-4">Start inviting professionals you know and trust</p>
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition"
                >
                  Send Your First Invitation
                </button>
              </div>
            ) : (
              <div className="divide-y">
                {invites.map((invite) => {
                  const statusConfig = STATUS_CONFIG[invite.status] || STATUS_CONFIG.pending;
                  const StatusIcon = statusConfig.icon;

                  return (
                    <div 
                      key={invite.invite_id}
                      className="p-4 hover:bg-gray-50 transition"
                      data-testid={`invite-${invite.invite_id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4 flex-1 min-w-0">
                          {/* Avatar/Icon */}
                          <div className={`w-12 h-12 ${statusConfig.bg} rounded-full flex items-center justify-center shrink-0`}>
                            <Briefcase className={statusConfig.color} size={20} />
                          </div>
                          
                          {/* Details */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-semibold text-gray-900 truncate">
                                {invite.professional_name}
                              </h3>
                              <Badge className={`${statusConfig.bg} ${statusConfig.color} ${statusConfig.border} border`}>
                                <StatusIcon size={12} className="mr-1" />
                                {statusConfig.label}
                              </Badge>
                            </div>
                            
                            <div className="flex items-center gap-4 mt-1 text-sm text-gray-500 flex-wrap">
                              <span className="flex items-center gap-1">
                                <Mail size={14} />
                                {invite.professional_email}
                              </span>
                              <span className="flex items-center gap-1">
                                <Briefcase size={14} />
                                {SPECIALTY_LABELS[invite.specialty] || invite.specialty}
                              </span>
                              <span className="flex items-center gap-1">
                                <MapPin size={14} />
                                {invite.city}{invite.region && `, ${invite.region}`}
                              </span>
                            </div>

                            <div className="text-xs text-gray-400 mt-2">
                              Invited on {formatDate(invite.created_at)}
                              {invite.email_sent && invite.email_sent_at && (
                                <span className="ml-2 text-green-600">• Email sent</span>
                              )}
                              {invite.signed_up_at && (
                                <span className="ml-2 text-green-600">• Signed up {formatDate(invite.signed_up_at)}</span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        {invite.status === 'pending' && (
                          <div className="flex items-center gap-2 shrink-0">
                            <button
                              onClick={() => handleResend(invite.invite_id)}
                              disabled={resendingId === invite.invite_id}
                              className="flex items-center gap-1 px-2 py-1.5 sm:p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition disabled:opacity-50 text-xs sm:text-sm"
                              title="Resend invitation"
                              data-testid={`resend-${invite.invite_id}`}
                            >
                              {resendingId === invite.invite_id ? (
                                <Loader2 className="animate-spin" size={18} />
                              ) : (
                                <RefreshCw size={18} />
                              )}
                              <span className="sm:hidden">Resend</span>
                            </button>
                            <button
                              onClick={() => handleCancel(invite.invite_id)}
                              disabled={cancellingId === invite.invite_id}
                              className="flex items-center gap-1 px-2 py-1.5 sm:p-2 text-red-600 hover:bg-red-50 rounded-lg transition disabled:opacity-50 text-xs sm:text-sm"
                              title="Cancel invitation"
                              data-testid={`cancel-${invite.invite_id}`}
                            >
                              {cancellingId === invite.invite_id ? (
                                <Loader2 className="animate-spin" size={18} />
                              ) : (
                                <Trash2 size={18} />
                              )}
                              <span className="sm:hidden">Cancel</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Milestone Reference */}
        <Card className="mt-8 border-0 shadow-md">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Gift size={20} className="text-amber-600" />
              Referral Rewards Program
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              {stats?.milestones?.map((milestone, idx) => (
                <div 
                  key={milestone.milestone_id}
                  className={`p-4 rounded-lg border-2 ${
                    stats.successful_referrals >= milestone.required_referrals
                      ? 'border-green-300 bg-green-50'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      stats.successful_referrals >= milestone.required_referrals
                        ? 'bg-green-200'
                        : 'bg-gray-100'
                    }`}>
                      {stats.successful_referrals >= milestone.required_referrals ? (
                        <CheckCircle className="text-green-600" size={16} />
                      ) : (
                        <span className="text-gray-500 font-semibold text-sm">{idx + 1}</span>
                      )}
                    </div>
                    <h4 className="font-semibold text-gray-900">{milestone.name}</h4>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{milestone.description}</p>
                  <div className="text-xs text-gray-500">
                    {milestone.required_referrals} referral{milestone.required_referrals > 1 ? 's' : ''} required
                  </div>
                  <div className="mt-2 text-sm font-medium text-amber-700">
                    Reward: {milestone.reward_type === 'discount' && `${milestone.reward_value}% off next month`}
                    {milestone.reward_type === 'free_month' && '1 month free'}
                    {milestone.reward_type === 'permanent_discount' && `${milestone.reward_value}% permanent discount + Badge`}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Invite Modal */}
      <InviteProfessionalModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSuccess={handleInviteSuccess}
      />
      
    </div>
  );
}

export default MyInvitations;
